// src/vestaboard.js
class Vestaboard extends HTMLElement {
    static get observedAttributes() { return ["rows","cols","cell","gap","speed","theme"]; }

    constructor(){
        super();
        const root = this.attachShadow({mode:'open'});
        this.canvas = document.createElement('canvas');
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.style.display = 'block';
        this.ctx = this.canvas.getContext('2d', { alpha: false });
        root.appendChild(this.canvas);

        // Config
        this.rows = parseInt(this.getAttribute('rows')||'7',10);
        this.cols = parseInt(this.getAttribute('cols')||'28',10);
        this.cell = parseInt(this.getAttribute('cell')||'24',10);
        this.gap  = parseInt(this.getAttribute('gap')||'4',10);
        this.speed = parseFloat(this.getAttribute('speed')||'1');
        this.theme = this.getAttribute('theme')||'dark';

        // Character set
        this.CHARSET = [..." ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,!?-:;#@•$%&()/\\'\"+"];

        // Tiles state
        this.tiles = new Array(this.rows*this.cols).fill(0).map(()=>({
            current: 0, target: 0, progress: 0, delay: 0, dir: 1
        }));

        // Playback state
        this._playing = false;
        this._frames = null; this._frameIdx = 0; this._nextFrameAt = 0; this._fps = 18; this._loop = false;

        this._last = performance.now();
        this._tick = this._tick.bind(this);
        this._resizeObserver = new ResizeObserver(()=>this._resize());
    }

    connectedCallback(){
        this._resizeObserver.observe(this);
        this._resize();
        requestAnimationFrame(this._tick);
    }
    disconnectedCallback(){ this._resizeObserver.disconnect(); }

    attributeChangedCallback(name){
        if(name==="rows"||name==="cols"||name==="cell"||name==="gap"){
            this.setConfig({
                rows: parseInt(this.getAttribute('rows')||this.rows,10),
                cols: parseInt(this.getAttribute('cols')||this.cols,10),
                cell: parseInt(this.getAttribute('cell')||this.cell,10),
                gap:  parseInt(this.getAttribute('gap')||this.gap,10),
            });
        }
        if(name==="speed"){ this.speed = parseFloat(this.getAttribute('speed')||this.speed); }
        if(name==="theme"){ this.theme = this.getAttribute('theme')||'dark'; }
    }

    // ---------- Public API ----------
    setConfig({rows, cols, cell, gap, speed} = {}){
        const r = rows ?? this.rows, c = cols ?? this.cols;
        if(r!==this.rows || c!==this.cols){
            this.rows=r; this.cols=c;
            this.tiles = new Array(this.rows*this.cols).fill(0).map(()=>({current:0,target:0,progress:0,delay:0, dir: 1}));
        }
        if(cell!==undefined) this.cell = cell;
        if(gap!==undefined)  this.gap = gap;
        if(speed!==undefined) this.speed = speed;
        this._resize();
    }
    setTheme(name){ this.theme = name; }

    setMessage(text, {stagger=0.0, wave=false, waveAmp=0.15, waveFx=0.7, waveFy=0.4} = {}){
        const chars = [...String(text).toUpperCase()];
        for(let r=0;r<this.rows;r++){
            for(let c=0;c<this.cols;c++){
                const i = r*this.cols + c;
                const code = chars[i] ?? ' ';
                const t = this.CHARSET.indexOf(code);
                this.tiles[i].target = t>=0 ? t : 0;

                const N = this.CHARSET.length;
                const tile = this.tiles[i];
                const ci = tile.current;
                const ti = tile.target;
                if (ci === ti) {
                  tile.progress = 0;
                  tile.dir = 1; // default
                } else {
                  const forward = (ti - ci + N) % N;    // steps going +1
                  const backward = (ci - ti + N) % N;   // steps going -1
                  tile.dir = backward < forward ? -1 : 1; // choose shortest path (tie -> +1)
                }

                const baseDelay = stagger*(r*this.cols+c);
                const wd = wave ? (Math.sin(c*waveFx + r*waveFy)*waveAmp) : 0;
                this.tiles[i].delay = Math.max(0, baseDelay + wd);
            }
        }
    }

    setInstantMessage(text){
      const chars = [...String(text).toUpperCase()];
      for(let r=0;r<this.rows;r++){
        for(let c=0;c<this.cols;c++){
          const i = r*this.cols + c;
          const code = chars[i] ?? ' ';
          const t = this.CHARSET.indexOf(code);
          const idx = t>=0 ? t : 0;
          const tile = this.tiles[i];
          tile.current = idx;
          tile.target = idx;
          tile.progress = 0;
          tile.delay = 0;
          tile.dir = 1;
        }
      }
    }

    // Convenience: set board from a 2D matrix of single-character strings
    setFromMatrix(matrix, { instant = true } = {}){
      if (!Array.isArray(matrix) || !matrix.length) return;
      const rows = Math.min(this.rows, matrix.length);
      const cols = Math.min(this.cols, matrix[0].length);
      for(let r=0;r<rows;r++){
        const row = matrix[r];
        for(let c=0;c<cols;c++){
          const ch = (row[c] || ' ').toUpperCase();
          const idx = this.CHARSET.indexOf(ch);
          const t = idx >= 0 ? idx : 0;
          const tile = this.tiles[r*this.cols + c];
          if (instant){
            tile.current = t;
            tile.target = t;
            tile.progress = 0;
            tile.delay = 0;
            tile.dir = 1;
          } else {
            tile.target = t;
            tile.delay = 0;
          }
        }
      }
    }

    playFrames(frames, {fps=18, loop=false, stagger=0.0} = {}){
        if(!frames || !frames.length) return;
        this._frames = frames.map(s=>String(s).toUpperCase());
        this._frameIdx = 0; this._fps = fps; this._loop = loop;
        this._nextFrameAt = 0; // trigger immediately
        this._playing = true;
        this.setMessage(this._frames[0], {stagger});
    }

    randomize(){
        for(let i=0;i<this.tiles.length;i++){
            this.tiles[i].target = Math.floor(Math.random()*this.CHARSET.length);
            this.tiles[i].delay = Math.random()*0.5;
        }
    }

    stop(){
      this._playing = false;
      for(const t of this.tiles){
        t.delay = 0;
        t.progress = 0;
      }
    }

    // ---------- Loop / Draw ----------
    _tick(now){
        const d = Math.min(50, now - this._last);
        this._last = now;
        this._step(d/1000);
        this._draw();
        requestAnimationFrame(this._tick);
    }

    _step(dt){
        const flipsPerSec = 12 * this.speed;
        for(const t of this.tiles){
            if(t.delay>0){ t.delay -= dt; continue; }
            const needFlip = t.current !== t.target;
            if(needFlip || t.progress>0){
                t.progress += dt * flipsPerSec;
                if(t.progress >= 1){
                    t.progress = 0;
                    if(t.current !== t.target){
                        const N = this.CHARSET.length;
                        t.current = (t.current + (t.dir || 1) + N) % N;
                        if (t.current === t.target) {
                          t.progress = 0;
                        }
                    }
                }
            }
        }

        if(this._playing){
            if(this._nextFrameAt <= performance.now()){
                const interval = 1000/this._fps;
                this._nextFrameAt = performance.now() + interval;
                this._frameIdx++;
                if(this._frameIdx >= this._frames.length){
                    if(this._loop){ this._frameIdx = 0; } else { this._playing=false; return; }
                }
                this.setMessage(this._frames[this._frameIdx]);
            }
        }
    }

    _palette(){
        switch(this.theme){
            case 'classic':
                return {bg:'#0c0d0f', frame:'#1b1e24', tile:'#0b0c10', text:'#f0c674', accent:'#33373f'};
            case 'color':
                return {bg:'#0c0d0f', frame:'#101318', tile:'#0b0c10',
                    text:['#f94144','#f3722c','#f9c74f','#90be6d','#43aa8b','#577590','#d65db1'],
                    accent:'#242833'};
            default:
                return {bg:'#0c0d0f', frame:'#101318', tile:'#0b0c10', text:'#cbd5e1', accent:'#242833'};
        }
    }

    _draw(){
        const ctx = this.ctx; if(!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const W = this.canvas.width, H = this.canvas.height;
        const {bg, frame, tile, text, accent} = this._palette();

        ctx.save();
        ctx.fillStyle = bg; ctx.fillRect(0,0,W,H);

        const cw = this.cell * dpr;
        const ch = this.cell * dpr * 1.4;
        const gap = this.gap * dpr;
        const bw = this.cols*cw + (this.cols-1)*gap;
        const bh = this.rows*ch + (this.rows-1)*gap;
        const ox = (W - bw)/2, oy = (H - bh)/2;

        // frame
        ctx.fillStyle = frame;
        this._roundRect(ctx, ox-gap*1.5, oy-gap*1.5, bw+gap*3, bh+gap*3, 12*dpr);
        ctx.fill();

        // text setup
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `${Math.floor(cw*0.7)}px Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto`;

        for(let r=0;r<this.rows;r++){
            for(let c=0;c<this.cols;c++){
                const i = r*this.cols + c;
                const t = this.tiles[i];
                const x = ox + c*(cw+gap);
                const y = oy + r*(ch+gap);

                // tile
                ctx.fillStyle = tile;
                this._roundRect(ctx, x, y, cw, ch, 6*dpr);
                ctx.fill();

                // flip illusion
                const prog = t.progress;
                const midFlip = prog > 0 && prog < 0.5;
                const N = this.CHARSET.length;
                const charIdx = midFlip ? (t.current + (t.dir || 1) + N) % N : t.current;
                const chStr = this.CHARSET[charIdx];

                ctx.save();
                ctx.translate(x+cw/2, y+ch/2);
                if(prog>0){
                    const phase = Math.sin(prog*Math.PI);    // 0→1→0
                    const scaleY = 1 - phase;                // squish
                    ctx.transform(1,0,0,Math.max(0.1,scaleY),0,0);
                }
                if(Array.isArray(text)){
                    ctx.fillStyle = text[r % text.length];
                } else {
                    ctx.fillStyle = text;
                }
                ctx.fillText(chStr, 0, this.cell*0.15*dpr);
                ctx.restore();

                // divider line
                ctx.strokeStyle = accent; ctx.lineWidth = 1*dpr;
                ctx.beginPath();
                ctx.moveTo(x+6*dpr, y+ch/2); ctx.lineTo(x+cw-6*dpr, y+ch/2);
                ctx.stroke();
            }
        }
        ctx.restore();
    }

    _resize(){
        const rect = this.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
        this.canvas.height= Math.max(1, Math.floor(rect.height * dpr));
    }

    _roundRect(ctx, x,y,w,h,r){
        const rr = Math.min(r, w/2, h/2);
        ctx.beginPath();
        ctx.moveTo(x+rr,y);
        ctx.arcTo(x+w,y,x+w,y+h,rr);
        ctx.arcTo(x+w,y+h,x,y+h,rr);
        ctx.arcTo(x,y+h,x,y,rr);
        ctx.arcTo(x,y,x+w,y,rr);
        ctx.closePath();
    }
}

if(!customElements.get('vestaboard-board')){
    customElements.define('vestaboard-board', Vestaboard);
}